import { Link } from "react-router-dom";
import {
  Github,
  Linkedin,
  Twitter,
  Facebook,
  Info,
  HelpCircle,
  Sparkles,
  BookOpen,
  Shield,
  FileText,
  Heart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { DiscordIcon } from "@/components/icons/DiscordIcon";
import { ThreadsIcon } from "@/components/icons/ThreadsIcon";
import { getRootUrl } from "@lyder-talk/core";
import { APP_NAME } from "@/lib/marketing-constants";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Github, href: "https://github.com/lyderdev", label: "GitHub" },
    { icon: Linkedin, href: "https://www.linkedin.com/in/l%E1%BB%A3i-v%C4%83n-645a09222/", label: "LinkedIn" },
    { icon: Twitter, href: "https://x.com/lyder_dev", label: "X" },
    { icon: Facebook, href: "https://www.facebook.com/lydertalk", label: "Facebook" },
    { icon: ThreadsIcon, href: "https://www.threads.com/@loicoder", label: "Threads" },
    { icon: DiscordIcon, href: "https://discord.gg/ACj3Ncag", label: "Discord" },
  ];

  const navigationLinks = [
    { icon: Sparkles, label: "Features", path: "/features" },
    { icon: Info, label: "About", path: "/about" },
    { icon: BookOpen, label: "Blog", path: "/blog" },
    { icon: HelpCircle, label: "FAQ", path: "/faq" },
  ];

  return (
    <footer className="mt-auto border-t border-border/60 bg-background">
      <div className="container mx-auto max-w-6xl px-4 py-10">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-2.5">
              <img
                src="/icon.png"
                alt=""
                className="h-8 w-8 rounded-lg"
                width={32}
                height={32}
              />
              <div>
                <p className="text-sm font-semibold text-foreground">{APP_NAME}</p>
                <p className="text-xs text-muted-foreground">Speaking practice, on tap.</p>
              </div>
            </div>
            <p className="max-w-xs text-sm text-muted-foreground leading-relaxed">
              AI-generated topics, recorded answers, and notes that help you speak with more confidence tomorrow than today.
            </p>
          </div>

          {/* Quick links */}
          <div className="space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Quick links
            </p>
            <ul className="space-y-1">
              {navigationLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <li key={link.path}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        window.open(getRootUrl(link.path), "_blank", "noopener,noreferrer")
                      }
                      className="h-8 w-full justify-start gap-2 px-2 text-sm text-muted-foreground hover:text-foreground"
                    >
                      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                      {link.label}
                    </Button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Socials */}
          <div className="space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Find us
            </p>
            <div className="flex flex-wrap gap-2">
              {socialLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={link.label}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground focus-ring"
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        <Separator className="my-8 bg-border/60" />

        <div className="flex flex-col items-start justify-between gap-3 text-xs text-muted-foreground sm:flex-row sm:items-center">
          <p>© {currentYear} {APP_NAME}. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link
              to="/privacy"
              className="inline-flex items-center gap-1.5 transition-colors hover:text-foreground"
            >
              <Shield className="h-3 w-3" aria-hidden="true" />
              Privacy
            </Link>
            <Link
              to="/terms"
              className="inline-flex items-center gap-1.5 transition-colors hover:text-foreground"
            >
              <FileText className="h-3 w-3" aria-hidden="true" />
              Terms
            </Link>
            <span className="inline-flex items-center gap-1.5">
              <Heart className="h-3 w-3 text-rose-500" aria-hidden="true" />
              Made for language learners
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
